import { useCallback, useEffect, useRef, useState } from "react";
import {
  CAMERA_TRACK_SLOTS,
  type CameraTrackSlot,
} from "../interface/webrtc_media_slots";
import type { CameraFpsMap } from "../types/camera_fps";

type ReceiverStatsSample = {
  frames: number | null;
  fps: number | null;
  timestampMs: number | null;
};

type ReceiverFpsMonitor = {
  active: boolean;
  intervalId: number;
  polling: boolean;
  previousSample: ReceiverStatsSample | null;
};

const RECEIVER_FPS_POLL_INTERVAL_MS = 1000;

function readFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readReceiverVideoStats(
  stats: RTCStatsReport,
): ReceiverStatsSample | null {
  let videoStats: ReceiverStatsSample | null = null;

  stats.forEach((report: any) => {
    const isVideoInbound =
      report.type === "inbound-rtp" &&
      (report.kind === "video" || report.mediaType === "video");

    if (!isVideoInbound || videoStats) {
      return;
    }

    videoStats = {
      frames:
        readFiniteNumber(report.framesDecoded) ??
        readFiniteNumber(report.framesReceived),
      fps: readFiniteNumber(report.framesPerSecond),
      timestampMs: readFiniteNumber(report.timestamp),
    };
  });

  return videoStats;
}

function calculateReceiverFps(
  previousSample: ReceiverStatsSample | null,
  nextSample: ReceiverStatsSample,
): number | null {
  if (nextSample.fps !== null) {
    return nextSample.fps;
  }

  if (
    !previousSample ||
    previousSample.frames === null ||
    previousSample.timestampMs === null ||
    nextSample.frames === null ||
    nextSample.timestampMs === null
  ) {
    return null;
  }

  const elapsedMs = nextSample.timestampMs - previousSample.timestampMs;
  const frameDelta = nextSample.frames - previousSample.frames;
  if (elapsedMs <= 0 || frameDelta < 0) {
    return null;
  }

  return (frameDelta * 1000) / elapsedMs;
}

function stopMonitor(
  monitors: Partial<Record<CameraTrackSlot, ReceiverFpsMonitor>>,
  slot: CameraTrackSlot,
): boolean {
  const monitor = monitors[slot];
  if (!monitor) {
    return false;
  }

  monitor.active = false;
  window.clearInterval(monitor.intervalId);
  delete monitors[slot];
  return true;
}

export function useCameraReceiverFps() {
  const [cameraReceiverFps, setCameraReceiverFps] = useState<CameraFpsMap>({});
  const receiverFpsMonitorMapRef = useRef<
    Partial<Record<CameraTrackSlot, ReceiverFpsMonitor>>
  >({});

  const setCameraSlotReceiverFps = useCallback(
    (slot: CameraTrackSlot, fps: number | null) => {
      setCameraReceiverFps((prev) => {
        if (prev[slot] === fps) {
          return prev;
        }

        return {
          ...prev,
          [slot]: fps,
        };
      });
    },
    [],
  );

  const stopReceiverFpsMonitor = useCallback(
    (slot: CameraTrackSlot) => {
      if (stopMonitor(receiverFpsMonitorMapRef.current, slot)) {
        setCameraSlotReceiverFps(slot, null);
      }
    },
    [setCameraSlotReceiverFps],
  );

  const startReceiverFpsMonitor = useCallback(
    (slot: CameraTrackSlot, receiver: RTCRtpReceiver) => {
      if (!receiver.getStats) {
        stopReceiverFpsMonitor(slot);
        return;
      }

      stopReceiverFpsMonitor(slot);

      const monitor: ReceiverFpsMonitor = {
        active: true,
        intervalId: 0,
        polling: false,
        previousSample: null,
      };
      receiverFpsMonitorMapRef.current[slot] = monitor;

      const pollReceiverFps = async () => {
        if (monitor.polling) {
          return;
        }

        monitor.polling = true;
        try {
          const sample = readReceiverVideoStats(await receiver.getStats());
          if (!monitor.active || !sample) {
            return;
          }

          const nextFps = calculateReceiverFps(
            monitor.previousSample,
            sample,
          );
          monitor.previousSample = sample;
          if (nextFps !== null) {
            setCameraSlotReceiverFps(slot, nextFps);
          }
        } catch (err) {
          if (!monitor.active) {
            return;
          }

          console.warn("[AdRemoteClient] Receiver FPS stats failed", {
            cameraSlot: slot,
            reason: err instanceof Error ? err.message : String(err),
          });
          stopReceiverFpsMonitor(slot);
        } finally {
          monitor.polling = false;
        }
      };

      void pollReceiverFps();
      monitor.intervalId = window.setInterval(() => {
        void pollReceiverFps();
      }, RECEIVER_FPS_POLL_INTERVAL_MS);
    },
    [setCameraSlotReceiverFps, stopReceiverFpsMonitor],
  );

  useEffect(() => {
    return () => {
      CAMERA_TRACK_SLOTS.forEach((slot) => {
        stopMonitor(receiverFpsMonitorMapRef.current, slot);
      });
    };
  }, []);

  return {
    cameraReceiverFps,
    startReceiverFpsMonitor,
    stopReceiverFpsMonitor,
  };
}
